import { DestroyRef, inject, Injectable } from '@angular/core';
import { collection, deleteDoc, doc, getDocs, getFirestore, onSnapshot, query, where } from '@angular/fire/firestore';
import { ChangeTyped, USERS_COLLECTION, USERS_PREFERENCES_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService, AuthenticationService, User } from '@damofire/data-security';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { PartialDivision } from './organization.dto';
import { Offline, Preference, PreferenceType } from './preference.dto';
import { UserPreferencesKey } from './user.dto';

@Injectable()
export abstract class PreferenceService {
  abstract offlineWarehouse(): Observable<PartialDivision>;
  abstract offlineWarehouses(): Observable<ChangeTyped<Offline>[]>;
  abstract setWarehouseAvailableOffline(warehouse: PartialDivision, available: boolean): Promise<void>;
  abstract updateUserPreference(preference: Preference<unknown>): Promise<void>;
  abstract listPreferences(key?: UserPreferencesKey): Observable<Preference<unknown>[]>;
  abstract getPreference(key?: UserPreferencesKey): Observable<Preference<unknown>>;
}

@Injectable()
export class FirebasePreferenceService implements PreferenceService {
  private readonly $offlineWarehouse: BehaviorSubject<PartialDivision> = new BehaviorSubject<PartialDivision>(null);
  private readonly $preferenceChanges: BehaviorSubject<ChangeTyped<Preference<unknown>>[]> = new BehaviorSubject([]);
  private readonly $allPreferences: BehaviorSubject<Preference<unknown>[]> = new BehaviorSubject([]);
  private readonly destroyRef = inject(DestroyRef);

  private offlinePreferencesSub: Subscription;
  private snapshotUnsub: () => void;
  constructor(
    private readonly auth: AuthenticationService,
    private readonly dtoSigner: AuthDtoSignerService,
  ) {
    const authSub = this.auth.subscribe({
      next: (user: User) => {
        this.loadAllFromUser(user);
        this.loadOffline(user);
      },
    });
    this.destroyRef.onDestroy(() => {
      authSub.unsubscribe();
      this.offlinePreferencesSub?.unsubscribe();
      this.snapshotUnsub?.();
    });
  }

  offlineWarehouse(): Observable<PartialDivision> {
    return this.$offlineWarehouse.pipe(distinctUntilChanged((w1, w2) => w1?._id === w2?._id));
  }

  offlineWarehouses(): Observable<ChangeTyped<Offline>[]> {
    return this.$preferenceChanges.pipe(map((changes) => changes.filter((p) => p.type === PreferenceType.OFFLINE) as ChangeTyped<Offline>[]));
  }

  async setWarehouseAvailableOffline(warehouse: PartialDivision, available: boolean): Promise<void> {
    const u = await this.auth.currentUser();
    const prefPath = `/${USERS_COLLECTION}/${u._id}/${USERS_PREFERENCES_COLLECTION}`;
    if (available) {
      const previousPref = await getDocs(query(collection(getFirestore(), prefPath), where('type', '==', 'offline')));
      for (const prefDoc of previousPref.docs) {
        await deleteDoc(prefDoc.ref);
      }

      const update: Offline = { user: u._id, key: UserPreferencesKey.OFFLINE_WAREHOUSE, value: { _id: warehouse._id, name: warehouse.name }, type: PreferenceType.OFFLINE };
      await this.dtoSigner.set(update, `${prefPath}/${warehouse._id}`);
    } else {
      await deleteDoc(doc(getFirestore(), `${prefPath}/${warehouse._id}`));
    }
  }

  async updateUserPreference(preference: Preference<unknown>): Promise<void> {
    const userPrefPath = `${USERS_COLLECTION}/${preference.user}/${USERS_PREFERENCES_COLLECTION}`;
    if (preference._id) {
      await this.dtoSigner.update(preference, `${userPrefPath}/${preference._id}`);
    } else {
      await this.dtoSigner.add(preference, userPrefPath);
    }
  }

  listPreferences(key?: UserPreferencesKey): Observable<Preference<unknown>[]> {
    return this.$allPreferences.pipe(map((prefs) => (key ? prefs.filter((p) => p.key === key) : prefs)));
  }

  getPreference(key?: UserPreferencesKey): Observable<Preference<unknown>> {
    return this.$allPreferences.pipe(
      map((prefs) => (key ? prefs.filter((p) => p.key === key) : prefs)),
      map((prefs) => prefs.at(-1)),
    );
  }

  private loadOffline(user: User): void {
    this.offlinePreferencesSub?.unsubscribe();
    if (!user) {
      this.$offlineWarehouse.next(null);
      return;
    }
    this.offlinePreferencesSub = this.$allPreferences
      .pipe(map((prefs) => prefs.find((p) => p.type === PreferenceType.OFFLINE)))
      .subscribe((pref) => this.$offlineWarehouse.next(pref?.value));
  }

  private loadAllFromUser(user: User): void {
    this.snapshotUnsub?.();
    if (!user) {
      this.$preferenceChanges.next([]);
      this.$allPreferences.next([]);
      return;
    }
    const q = query(collection(getFirestore(), `/${USERS_COLLECTION}/${user._id}/${USERS_PREFERENCES_COLLECTION}`));
    this.snapshotUnsub = onSnapshot(q, (snap) => {
      const changeSet = snap.docChanges().map(
        (change) =>
          ({
            ...change.doc.data(),
            _id: change.doc.id,
            changeType: change.type,
          }) as unknown as ChangeTyped<Preference<unknown>>,
      );
      this.$preferenceChanges.next(changeSet);

      const allPrefs = snap.docs.map((d) => ({ ...d.data(), _id: d.id }) as unknown as Preference<unknown>);
      this.$allPreferences.next(allPrefs);
    });
  }
}
